import contributorRoles from "astrid-firestore/src/api/articles/constants/contributorRoles";
import { teamRoles } from "astrid-firestore/src/constants/teamRoles";
import { getCollectionData } from "astrid-firestore/src/helpers";
import arrayChunk from "astrid-helpers/src/arrayChunk";
import mapThemaToBisac from "astrid-helpers/src/themaToBisac/themaToBisac";

import articleProductionStatuses from "../../src/api/articles/constants/articleProductionStatuses";
import { articleProductionStatus } from "../../src/api/articles/types/Article";
import productionStatuses from "../../src/api/productions/constants/productionStatuses";

import { hasMulti } from "./migrateProductionToFlattening";

async function mapAuthors({ api, env }, { production }) {
	let allAuthors = [];
	for (const authorChunk of arrayChunk(production.author || [], 10)) {
		const authors = await getCollectionData(
			api[env].db.collection("people").where(api.admin.firestore.FieldPath.documentId(), "in", authorChunk),
		);
		allAuthors = allAuthors.concat(authors);
	}

	return allAuthors.map((author) => {
		return {
			firstName: author.firstName,
			lastName: author.lastName,
			name: `${author.firstName} ${author.lastName}`,
			role: contributorRoles.AUTHOR,
		};
	});
}

async function mapTranslators({ api, env }, { production }) {
	let allTranslators = [];
	for (const translatorChunk of arrayChunk(production.translator || [], 10)) {
		const translators = await getCollectionData(
			api[env].db.collection("people").where(api.admin.firestore.FieldPath.documentId(), "in", translatorChunk),
		);
		allTranslators = allTranslators.concat(translators);
	}

	return allTranslators.map((translator) => {
		return {
			firstName: translator.firstName,
			lastName: translator.lastName,
			name: `${translator.firstName} ${translator.lastName}`,
			role: contributorRoles.TRANSLATOR,
		};
	});
}

async function mapNarrators({ api, env }, { production }) {
	if (["external", "subcontract", "backlist"].includes(production.productionType)) {
		let allNarrators = [];
		for (const readerChunk of arrayChunk(production.reader || [], 10)) {
			const readers = await getCollectionData(
				api[env].db.collection("users").where(api.admin.firestore.FieldPath.documentId(), "in", readerChunk),
			);
			allNarrators = allNarrators.concat(readers);
		}

		return allNarrators.map((author) => {
			return {
				firstName: author.firstName,
				lastName: author.lastName,
				name: `${author.firstName} ${author.lastName}`,
				role: contributorRoles.NARRATOR,
			};
		});
	} else {
		return Object.values(production.team || {})
			?.filter((teamMember) => teamMember?.role === teamRoles.READER)
			.map((narrator) => {
				return {
					firstName: narrator.user.firstName,
					lastName: narrator.user.lastName,
					name: `${narrator.user.firstName} ${narrator.user.lastName}`,
					role: contributorRoles.NARRATOR,
				};
			});
	}
}

async function mapContributors({ api, env }, { production }) {
	const authors = await mapAuthors({ api, env }, { production });
	const translators = await mapTranslators({ api, env }, { production });
	const narrators = await mapNarrators({ api, env }, { production });
	return [...authors, ...narrators, ...translators].filter(
		(contributor) => contributor.firstName || contributor.lastName,
	);
}

export default async function createCommonArticleData({ api, env }, { production, season }) {
	const contributors = await mapContributors({ api, env }, { production });
	return {
		contributors,
		language: production.language,
		originalLanguage: production.languageOriginal,
		additionalLanguages: production.languageExtra || [],
		originalName: "", // does not exist in old productions
		themaCodes: production.genreEditeur || [],
		bisacCodes: mapThemaToBisac(production.genreEditeur || [])?.matches?.map((match) => match.bisac) || [],
		tags: production.tags,
		abridged: Boolean(production.abridged),
		deliveryDate: production.deliveryDate ? api.Timestamp(production.deliveryDate) : null,
		productionStatus:
			production.status === productionStatuses.DONE
				? articleProductionStatuses.COMPLETED
				: articleProductionStatuses.NOT_STARTED,
		title: false,
		titleSync: false,
		season,
		serieNumber: hasMulti(production) ? null : Number(production.part) || null,
	};
}
